import { useMemo, useState } from "react";
import { Search, Clock, Plus, Check, TrendingUp } from "lucide-react";
import { FILTERS, typeMeta, MONTHS } from "../../lib/adventureConstants";

export default function ModuleLibrary({ modules, locationsById, onAdd, onOpenDetail, addedIds, trip, serviceLevel }) {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    return modules.filter((m) => {
      const matchType = filter === "all" ? true
        : filter === "trekking" ? (m.type === "trekking" || m.type === "trek")
        : m.type === filter;
      const matchQ = !q || m.name.toLowerCase().includes(q.toLowerCase());
      return matchType && matchQ;
    });
  }, [modules, filter, q]);

  return (
    <div className="flex flex-col h-full bg-[#FAFAFA]" data-testid="module-library">
      <div className="p-5 border-b border-sand-dark">
        <h2 className="font-display font-bold text-lg tracking-tight mb-0.5">Experience Library</h2>
        <p className="font-mono text-xs text-ink-muted uppercase tracking-widest">{filtered.length} modules available</p>
        <div className="relative mt-3">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            data-testid="module-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search experiences…"
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-sand-dark rounded-sm focus:outline-none focus:ring-2 focus:ring-terracotta"
          />
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {FILTERS.map((f) => (
            <button
              key={f.id} data-testid={`filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                filter === f.id ? "bg-moss text-white border-moss" : "bg-white text-ink-secondary border-sand-dark hover:border-moss"
              }`}
            >{f.label}</button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto thin-scroll p-4 space-y-6">
        {filtered.map((m) => {
          const meta = typeMeta(m.type);
          const Icon = meta.icon;
          const added = addedIds.includes(m.id);
          const available = !trip?.month || m.months.includes(trip.month);
          return (
            <div
              key={m.id}
              data-testid={`module-card-${m.id}`}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData("moduleId", m.id);
                e.currentTarget.classList.add("dragging");
              }}
              onDragEnd={(e) => e.currentTarget.classList.remove("dragging")}
              className="group cursor-grab active:cursor-grabbing"
            >
              <div className="relative overflow-hidden rounded-sm" onClick={() => onOpenDetail(m)}>
                <img src={m.image} alt={m.name}
                  className="w-full h-40 object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/50 to-transparent" />
                <span className="absolute top-2.5 left-2.5 flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold text-white"
                  style={{ background: meta.color }}>
                  <Icon className="w-3 h-3" /> {meta.label}
                </span>
                {!available && (
                  <span className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-white/90 text-destructive">
                    Off-season
                  </span>
                )}
              </div>

              <div className="mt-2.5">
                <div className="flex items-start justify-between gap-2">
                  <button onClick={() => onOpenDetail(m)} className="text-left font-display font-bold text-[15px] tracking-tight leading-snug hover:text-terracotta transition-colors">
                    {m.name}
                  </button>
                </div>
                <p className="text-xs text-ink-muted mt-0.5">{locationsById[m.location_id]?.name}</p>

                <div className="flex items-center gap-3 mt-2 font-mono text-[11px] text-ink-secondary">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{m.days} {m.days === 1 ? "DAY" : "DAYS"}</span>
                  <span>·</span>
                  <span>{m.difficulty.toUpperCase()}</span>
                </div>

                <div className="flex items-center justify-between mt-3">
                  <div>
                    <span className="font-mono text-[10px] text-ink-muted uppercase tracking-widest flex items-center gap-1"><TrendingUp className="w-3 h-3" />from</span>
                    <span className="font-mono font-semibold text-sm">${m.prices[serviceLevel] ?? m.prices.essential}</span>
                  </div>
                  <button
                    data-testid={`add-module-${m.id}`}
                    onClick={() => onAdd(m)}
                    disabled={added}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-sm text-xs font-semibold tracking-wide transition-colors ${
                      added ? "bg-moss-light text-moss cursor-default" : "bg-terracotta text-white hover:bg-terracotta-hover"
                    }`}
                  >
                    {added ? <><Check className="w-3.5 h-3.5" /> Added</> : <><Plus className="w-3.5 h-3.5" /> Add</>}
                  </button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {m.months.slice(0, 6).map((mo) => (
                    <span key={mo} className="font-mono text-[9px] px-1 py-0.5 rounded-sm bg-sand text-ink-muted">{MONTHS[mo - 1]}</span>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
